import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';

// ── Static assortment list — all active assortments for pricing purposes.
// Independent of scan history so new assortments appear immediately.
const ASSORTMENTS = [
  'Bhuja 40-50g',
  'Bhuja 70-200g',
  'Bhuja 300g+',
  'Bhuja Authentic',
  'Bhuja Multipack 125g',
  'Infuzions 90-110g',
  'Infuzions Air Fries 90g',
  'Infuzions Air Fries Multipack 75g',
  'Infuzions Multipack 75g',
];

const CUSTOMERS = ['WW', 'Coles'];

const TS_ACTIVITIES = ['Price Promotion', 'Customer Marketing', 'Slotting', 'Co-op', 'Other'];
const TS_BRANDS = ['Bhuja Core', 'Infuzions Core', 'Infuzions Multipack', 'Infuzions Air Fries', 'Infuzions Other', 'All Brands'];
const TS_DEPARTMENTS = ['Sales', 'Marketing', 'Finance'];
const TS_FYS = ['2025', '2026', '2027'];

function newTsEntry(custCode = '', custName = '') {
  return {
    id: `ts-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    department: 'Sales',
    project: '',
    custCode,
    custName,
    activity: 'Price Promotion',
    brand: 'Bhuja Core',
    from: '',
    to: '',
    units: null,
    unitDeal: null,
    variable: null,
    fixed: null,
    total: null,
    fy: '2026',
    responsibility: '',
    comments: '',
  };
}

function calcTsVariable(entry) {
  const u = parseFloat(entry.units);
  const d = parseFloat(entry.unitDeal);
  if (!isNaN(u) && !isNaN(d) && u > 0 && d > 0) return u * d;
  return 0;
}

function calcTsTotal(entry) {
  const v = calcTsVariable(entry);
  const f = parseFloat(entry.fixed) || 0;
  const t = v + f;
  return t > 0 ? t : null;
}

// ── #3 typed-table reshape: API rows <-> the page's terms/tsEntries shapes ──
const CODE_BY_CUST = { WW: 'CUS0044', Coles: 'CUS0027' };
const CUST_BY_CODE = { CUS0044: 'WW', CUS0027: 'Coles' };
const numStr = (v) => (v == null ? '' : String(v));

// trade-term rows -> { [WW|Coles]: { [assortment]: {rrp,listPrice,oiDisc,dtt1,dtt2,dtt3,marginalCost} } }
function reshapeTerms(rows) {
  const t = {};
  for (const r of rows || []) {
    const cust = CUST_BY_CODE[r.customerCode] || r.customerCode;
    (t[cust] ||= {})[r.assortment] = {
      rrp: numStr(r.rrp), listPrice: numStr(r.listPrice), oiDisc: numStr(r.oiDiscPct),
      dtt1: numStr(r.dtt1Pct), dtt2: numStr(r.dtt2Pct), dtt3: numStr(r.dtt3Amount), marginalCost: numStr(r.marginalCost),
    };
  }
  return t;
}

// trade-spend rows -> the entry shape the table renders (id is the numeric PK as a string)
function reshapeSpend(rows) {
  return (rows || []).map((r) => ({
    id: String(r.tradeSpendId),
    department: r.department || 'Sales', project: r.project || '',
    custCode: r.customerCode, custName: r.customerName || r.customerCode,
    activity: r.activity || 'Price Promotion', brand: r.brand || 'Bhuja Core',
    from: r.fromDate || '', to: r.toDate || '',
    units: r.units, unitDeal: r.unitDeal, variable: null, fixed: r.fixedAmount, total: null,
    fy: r.fy != null ? String(r.fy) : '2026', responsibility: r.responsibility || '', comments: r.comments || '',
  }));
}

// ── Derived calculations ───────────────────────────────────────────────────────
function calcRow(t) {
  const listPrice    = parseFloat(t.listPrice)    || null;
  const rrpIncGst    = parseFloat(t.rrp)          || null;
  const oiDisc       = parseFloat(t.oiDisc)       || 0;   // %
  const dtt1         = parseFloat(t.dtt1)         || 0;   // % off invoice
  const dtt2         = parseFloat(t.dtt2)         || 0;   // % off invoice
  const dtt3         = parseFloat(t.dtt3)         || 0;   // $ flat deduction
  const marginalCost = parseFloat(t.marginalCost) || null;

  // RRP is GST-inclusive — strip 10% GST for retailer margin calc (ex-GST basis)
  const rrp = rrpIncGst != null ? rrpIncGst / 1.1 : null;

  const invoicePrice = listPrice != null ? listPrice * (1 - oiDisc / 100) : null;
  const dttPctAmount = invoicePrice != null ? invoicePrice * (dtt1 + dtt2) / 100 : 0;
  const netSellPrice = invoicePrice != null ? invoicePrice - dttPctAmount - dtt3 : null;

  const retailerMarginPct = (rrp != null && netSellPrice != null && rrp > 0)
    ? ((rrp - netSellPrice) / rrp) * 100 : null;
  const ourMarginPct  = (netSellPrice != null && marginalCost != null && netSellPrice > 0)
    ? ((netSellPrice - marginalCost) / netSellPrice) * 100 : null;
  const ourMarginDollar = (netSellPrice != null && marginalCost != null)
    ? netSellPrice - marginalCost : null;

  return { rrpExGst: rrp, invoicePrice, netSellPrice, retailerMarginPct, ourMarginPct, ourMarginDollar };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt2  = v => v != null ? `$${v.toFixed(2)}` : '—';
const fmtPct = v => v != null ? `${v.toFixed(1)}%` : '—';

function pctColor(v, invert = false) {
  if (v == null) return 'var(--text-muted)';
  const good = invert ? v < 0 : v > 0;
  if (Math.abs(v) < 1) return 'var(--text-muted)';
  return good ? 'var(--success)' : 'var(--danger)';
}

function NumInput({ value, onChange, placeholder, prefix, suffix, min, max, step = '0.01', dp = 2 }) {
  const [focused, setFocused] = useState(false);
  // Show raw value while editing, formatted to dp on blur
  const display = focused || value === '' ? value : (parseFloat(value) || 0) !== 0 ? parseFloat(value).toFixed(dp) : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {prefix && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{prefix}</span>}
      <input
        type="number"
        value={display}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder || '—'}
        min={min}
        max={max}
        step={step}
        style={{
          width: 72,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-soft)',
          borderRadius: 5,
          color: 'var(--text-primary)',
          fontSize: 12,
          padding: '4px 6px',
          textAlign: 'right',
        }}
      />
      {suffix && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{suffix}</span>}
    </div>
  );
}

// ── Waterfall panel ───────────────────────────────────────────────────────────
function WaterfallPanel({ label, terms, customer }) {
  const t = terms;
  const c = calcRow(t);
  const rrpIncGst    = parseFloat(t.rrp)          || null;
  const listPrice    = parseFloat(t.listPrice)    || null;
  const oiDisc       = parseFloat(t.oiDisc)       || 0;
  const dtt1         = parseFloat(t.dtt1)         || 0;
  const dtt2         = parseFloat(t.dtt2)         || 0;
  const dtt3         = parseFloat(t.dtt3)         || 0;
  const marginalCost = parseFloat(t.marginalCost) || null;

  const rows = [
    { label: `RRP incl. GST`,        value: rrpIncGst,            color: 'var(--text-primary)',  indent: 0 },
    { label: `RRP ex. GST`,          value: c.rrpExGst,           color: 'var(--text-primary)',  indent: 1, note: '÷1.1' },
    { label: 'List Price',           value: listPrice,            color: 'var(--text-primary)',  indent: 0 },
    { label: `OI Discount (${oiDisc}%)`, value: listPrice != null && oiDisc ? -(listPrice * oiDisc / 100) : null, color: 'var(--warn)', indent: 1 },
    { label: 'Invoice Price',        value: c.invoicePrice,       color: 'var(--info)',  indent: 0, bold: true },
    { label: `DTT 1 (${dtt1}%)`,     value: c.invoicePrice != null && dtt1 ? -(c.invoicePrice * dtt1 / 100) : null, color: 'var(--warn)', indent: 1 },
    { label: `DTT 2 (${dtt2}%)`,     value: c.invoicePrice != null && dtt2 ? -(c.invoicePrice * dtt2 / 100) : null, color: 'var(--warn)', indent: 1 },
    { label: `DTT 3 ($${dtt3.toFixed(2)})`, value: dtt3 > 0 ? -dtt3 : null,                                        color: 'var(--warn)', indent: 1 },
    { label: 'Net Sell Price',       value: c.netSellPrice,       color: 'var(--success)',  indent: 0, bold: true },
    { label: 'Marginal Cost',        value: marginalCost != null ? -marginalCost : null, color: 'var(--danger)', indent: 1 },
    { label: 'Contribution Margin',  value: c.ourMarginDollar,    color: c.ourMarginDollar != null && c.ourMarginDollar >= 0 ? 'var(--success)' : 'var(--danger)', indent: 0, bold: true },
  ].filter(r => r.value != null);

  const maxAbs = Math.max(...rows.map(r => Math.abs(r.value)), 0.01);

  return (
    <div className="card" style={{ minWidth: 280 }}>
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>{customer}</div>

      {rows.map((r, i) => (
        <div key={i} style={{ marginBottom: 6, paddingLeft: r.indent * 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: r.bold ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: r.bold ? 600 : 400 }}>{r.label}</span>
            <span style={{ fontSize: 12, fontWeight: r.bold ? 700 : 400, color: r.value < 0 ? 'var(--warn)' : r.color }}>
              {r.value < 0 ? `-$${Math.abs(r.value).toFixed(2)}` : `$${r.value.toFixed(2)}`}
            </span>
          </div>
          <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.min((Math.abs(r.value) / maxAbs) * 100, 100)}%`,
              background: r.value < 0 ? 'var(--warn)' : r.color,
              borderRadius: 2,
            }} />
          </div>
        </div>
      ))}

      {/* Summary metrics */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Retailer Margin</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: pctColor(c.retailerMarginPct) }}>{fmtPct(c.retailerMarginPct)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>on RRP ex-GST</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Our Margin</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: pctColor(c.ourMarginPct) }}>{fmtPct(c.ourMarginPct)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>on net sell price</div>
        </div>
      </div>
    </div>
  );
}

// ── Default empty terms ───────────────────────────────────────────────────────
function emptyTerms() {
  return { rrp: '', listPrice: '', oiDisc: '', dtt1: '', dtt2: '', dtt3: '', marginalCost: '' };
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Commercials() {
  const apiFetch = useApi();
  const queryClient = useQueryClient();
  const [customer, setCustomer] = useState('WW');
  const [selectedAssortment, setSelectedAssortment] = useState(ASSORTMENTS[0]);

  // ── Typed data (#3): trade terms + trade spend from commercial.* tables ──
  // No default value: termRows stays undefined until the query resolves, so the
  // hydrate-once effect below waits for REAL data instead of firing on an empty [].
  const { data: termRows, error: termsError, refetch: refetchTerms, isFetching: isLoadingTerms } = useQuery({
    queryKey: ['trade-terms'],
    queryFn: async () => { const r = await apiFetch('/trade-terms'); if (!r.ok) throw new Error(`Failed to load trade terms: ${r.status}`); return r.json(); },
    refetchOnMount: 'always', retry: false,
  });
  const { data: spendRows = [], error: spendError, refetch: refetchSpend, isFetching: isLoadingSpend } = useQuery({
    queryKey: ['trade-spend'],
    queryFn: async () => { const r = await apiFetch('/trade-spend'); if (!r.ok) throw new Error(`Failed to load trade spend: ${r.status}`); return r.json(); },
    refetchOnMount: 'always', retry: false,
  });

  const serverError = termsError || spendError;
  const isLoadingCommercials = isLoadingTerms || isLoadingSpend;
  const refetchCommercials = () => { refetchTerms(); refetchSpend(); };

  // Trade-spend rows drive the table directly (no local array state).
  const tsEntries = useMemo(() => reshapeSpend(spendRows), [spendRows]);

  // Trade terms kept as a local editing buffer (instant typing); hydrate once.
  const [terms, setTerms] = useState({});
  const hasHydrated = useRef(false);
  const lastSavedTermsRef = useRef({});
  useEffect(() => {
    if (hasHydrated.current || !termRows) return;
    hasHydrated.current = true;
    const t = reshapeTerms(termRows);
    setTerms(t);
    lastSavedTermsRef.current = JSON.parse(JSON.stringify(t));
  }, [termRows]);

  // Per-cell trade-term upsert.
  const saveTermMut = useMutation({
    mutationFn: async ({ customerCode, assortment, cell }) => {
      const r = await apiFetch('/trade-term', { method: 'PUT', body: JSON.stringify({ customerCode, assortment, ...cell }) });
      if (!r.ok) throw new Error(`Save term failed: HTTP ${r.status}`);
      return r.json();
    },
    onError: (e) => { console.error('[Commercials] term save error', e); },
  });
  const saveTermRef = useRef(saveTermMut.mutate);
  saveTermRef.current = saveTermMut.mutate;

  // Debounced diff-save: 1s after the last edit, PUT only the changed term cells.
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const prev = lastSavedTermsRef.current || {};
      for (const cust of Object.keys(terms)) {
        for (const asst of Object.keys(terms[cust] || {})) {
          if (JSON.stringify(terms[cust][asst]) !== JSON.stringify(prev?.[cust]?.[asst] || {})) {
            saveTermRef.current({ customerCode: CODE_BY_CUST[cust] || cust, assortment: asst, cell: terms[cust][asst] });
          }
        }
      }
      lastSavedTermsRef.current = JSON.parse(JSON.stringify(terms));
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [terms]);

  // Trade-spend mutations (per row).
  const invalidateSpend = () => queryClient.invalidateQueries({ queryKey: ['trade-spend'] });
  const addSpendMut = useMutation({
    mutationFn: async (entry) => { const r = await apiFetch('/trade-spend', { method: 'POST', body: JSON.stringify(entry) }); if (!r.ok) throw new Error(`Add failed: HTTP ${r.status}`); return r.json(); },
    onSuccess: invalidateSpend,
  });
  const updateSpendMut = useMutation({
    mutationFn: async (entry) => { const r = await apiFetch(`/trade-spend/${entry.id}`, { method: 'PUT', body: JSON.stringify(entry) }); if (!r.ok) throw new Error(`Update failed: HTTP ${r.status}`); return r.json(); },
    onSuccess: invalidateSpend,
    onError: (e) => { console.error('[Commercials] spend update error', e); },
  });
  const deleteSpendMut = useMutation({
    mutationFn: async (id) => { const r = await apiFetch(`/trade-spend/${id}`, { method: 'DELETE' }); if (!r.ok) throw new Error(`Delete failed: HTTP ${r.status}`); return r.json(); },
    onSuccess: invalidateSpend,
  });

  const [tsEditId, setTsEditId] = useState(null);
  const [tsEditDraft, setTsEditDraft] = useState(null);
  const [tsCustFilter, setTsCustFilter] = useState('');
  const [tsFyFilter, setTsFyFilter] = useState('2026');
  const [tsActivityFilter, setTsActivityFilter] = useState('All');
  const [tsSaved, setTsSaved] = useState(false);

  function tsStartEdit(entry) { setTsEditId(entry.id); setTsEditDraft({ ...entry }); }
  function tsCancelEdit() { setTsEditId(null); setTsEditDraft(null); }
  function tsCommitEdit() {
    const updated = { ...tsEditDraft, total: calcTsTotal(tsEditDraft) };
    updateSpendMut.mutate(updated);
    setTsEditId(null); setTsEditDraft(null);
    setTsSaved(true); setTimeout(() => setTsSaved(false), 1500);
  }
  function tsAddEntry() {
    const entry = newTsEntry(
      customer === 'WW' ? 'CUS0044' : 'CUS0027',
      customer === 'WW' ? 'Woolworths' : 'Coles Supermarkets',
    );
    addSpendMut.mutate(entry, {
      onSuccess: (res) => {
        const id = res?.id != null ? String(res.id) : null;
        if (!id) return;
        // Optimistically add so the new row opens in edit mode immediately;
        // the onSuccess invalidate refetch then replaces it with canonical data.
        queryClient.setQueryData(['trade-spend'], (old = []) => [
          ...old,
          { tradeSpendId: res.id, customerCode: entry.custCode, customerName: entry.custName, department: entry.department,
            project: '', activity: entry.activity, brand: entry.brand, fromDate: null, toDate: null, units: null,
            unitDeal: null, fixedAmount: null, fy: Number(entry.fy), responsibility: '', comments: '' },
        ]);
        setTsEditId(id);
        setTsEditDraft({ ...entry, id });
      },
    });
  }
  function tsDeleteEntry(id) {
    if (!window.confirm('Remove this trade spend entry?')) return;
    deleteSpendMut.mutate(id);
  }

  const tsFiltered = useMemo(() => {
    let es = tsEntries;
    if (tsCustFilter) es = es.filter(e => e.custCode === tsCustFilter || e.custName.toLowerCase().includes(tsCustFilter.toLowerCase()));
    if (tsFyFilter !== 'All') es = es.filter(e => e.fy === tsFyFilter);
    if (tsActivityFilter !== 'All') es = es.filter(e => e.activity === tsActivityFilter);
    return es;
  }, [tsEntries, tsCustFilter, tsFyFilter, tsActivityFilter]);

  const tsTotals = useMemo(() => ({
    variable: tsFiltered.reduce((s, e) => s + calcTsVariable(e), 0),
    fixed: tsFiltered.reduce((s, e) => s + (parseFloat(e.fixed) || 0), 0),
    total: tsFiltered.reduce((s, e) => s + (calcTsTotal(e) || 0), 0),
  }), [tsFiltered]);

  // Unique customers in trade spend data
  const tsCustomers = useMemo(() => {
    const map = {};
    tsEntries.forEach(e => { if (e.custCode) map[e.custCode] = e.custName; });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tsEntries]);

  const assortments = ASSORTMENTS;

  function getTerms(cust, assortment) {
    return terms[cust]?.[assortment] || emptyTerms();
  }

  function setField(assortment, field, value) {
    setTerms(prev => ({
      ...prev,
      [customer]: {
        ...(prev[customer] || {}),
        [assortment]: {
          ...getTerms(customer, assortment),
          [field]: value,
        },
      },
    }));
  }

  function clearAll() {
    if (!window.confirm(`Clear all terms for ${customer === 'WW' ? 'Woolworths' : 'Coles'}?`)) return;
    const existing = Object.keys(terms[customer] || {});
    setTerms(prev => ({ ...prev, [customer]: {} }));
    // Persist the clear: write empty cells for previously-populated assortments.
    for (const asst of existing) {
      saveTermRef.current({ customerCode: CODE_BY_CUST[customer] || customer, assortment: asst, cell: emptyTerms() });
    }
    lastSavedTermsRef.current = { ...lastSavedTermsRef.current, [customer]: {} };
  }

  const customerLabel = customer === 'WW' ? 'Woolworths' : 'Coles';

  // Column header style
  const th = { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' };
  const thLeft = { ...th, textAlign: 'left' };
  const td = { padding: '7px 10px', borderBottom: '1px solid var(--bg-card)', verticalAlign: 'middle' };
  const tdRight = { ...td, textAlign: 'right' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Load-failure banner: hydration never completed, so edits won't save.
          Matches the CRM.jsx Phase 14 fix. */}
      {serverError && !hasHydrated.current && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, color: 'var(--danger)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13 }}>
            <strong>Could not load your commercials data.</strong>{' '}
            <span style={{ color: 'var(--danger)' }}>Any edits you make below will <u>not be saved</u> until this loads successfully.</span>
            <div style={{ fontSize: 11, marginTop: 4, color: 'var(--danger)', opacity: 0.85 }}>
              {String(serverError?.message || serverError).slice(0, 200)}
            </div>
          </div>
          <button onClick={() => refetchCommercials()} disabled={isLoadingCommercials}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: isLoadingCommercials ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {isLoadingCommercials ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>Commercial Terms</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Customer pricing &amp; trade terms by assortment · shared, saved to the database · PLANAUDIT integration coming
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {CUSTOMERS.map(c => (
            <button
              key={c}
              className={customer === c ? 'pill-active' : 'pill-inactive'}
              onClick={() => setCustomer(c)}
            >
              {c === 'WW' ? 'Woolworths' : 'Coles'}
            </button>
          ))}
          <button
            onClick={clearAll}
            style={{ background: 'transparent', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-muted)', fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}
          >
            Clear {customerLabel}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {[
          { dot: 'var(--text-primary)', label: 'Input field' },
          { dot: 'var(--info)', label: 'Invoice Price = List × (1 − OI%)' },
          { dot: 'var(--success)', label: 'Net Sell = Invoice × (1 − DTT1% − DTT2%) − DTT3$' },
          { dot: 'var(--ai)', label: 'Retailer Margin = (RRP ex-GST − Net Sell) / RRP ex-GST' },
          { dot: 'var(--warn)', label: 'Our Margin = (Net Sell − Cost) / Net Sell' },
        ].map(({ dot, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Main layout: table + waterfall */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

        {/* Terms table */}
        <div className="card" style={{ flex: 1, minWidth: 0, padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={thLeft}>Assortment</th>
                <th style={th}>RRP (incl GST)</th>
                <th style={th}>List Price</th>
                <th style={th}>OI Disc %</th>
                <th style={{ ...th, color: 'var(--info)' }}>Invoice Price</th>
                <th style={th}>DTT 1 %</th>
                <th style={th}>DTT 2 %</th>
                <th style={th}>DTT 3 $</th>
                <th style={{ ...th, color: 'var(--success)' }}>Net Sell</th>
                <th style={th}>Marginal Cost</th>
                <th style={{ ...th, color: 'var(--ai)' }}>Ret. Margin</th>
                <th style={{ ...th, color: 'var(--warn)' }}>Our Margin</th>
              </tr>
            </thead>
            <tbody>
              {assortments.map(assortment => {
                const t   = getTerms(customer, assortment);
                const c   = calcRow(t);
                const isSelected = selectedAssortment === assortment;

                return (
                  <tr
                    key={assortment}
                    onClick={() => setSelectedAssortment(assortment)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? 'var(--bg-elevated)' : 'transparent',
                      borderLeft: isSelected ? '2px solid var(--info)' : '2px solid transparent',
                    }}
                  >
                    {/* Assortment name */}
                    <td style={{ ...td, fontWeight: 500, color: 'var(--text-primary)', fontSize: 12, paddingLeft: isSelected ? 10 : 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                          background: assortment.startsWith('Bhuja') ? 'var(--warn)' : 'var(--info)',
                        }} />
                        {assortment}
                      </div>
                    </td>

                    {/* RRP */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.rrp} onChange={v => setField(assortment, 'rrp', v)} prefix="$" placeholder="0.00" min="0" />
                    </td>

                    {/* List Price */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.listPrice} onChange={v => setField(assortment, 'listPrice', v)} prefix="$" placeholder="0.00" min="0" />
                    </td>

                    {/* OI Discount % */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.oiDisc} onChange={v => setField(assortment, 'oiDisc', v)} suffix="%" placeholder="0.0" min="0" max="100" step="0.1" />
                    </td>

                    {/* Invoice Price — calculated */}
                    <td style={{ ...tdRight, color: c.invoicePrice != null ? 'var(--info)' : 'var(--border-soft)', fontWeight: 600, fontSize: 12 }}>
                      {fmt2(c.invoicePrice)}
                    </td>

                    {/* DTT 1 % */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.dtt1} onChange={v => setField(assortment, 'dtt1', v)} suffix="%" placeholder="0.0" min="0" max="100" step="0.1" />
                    </td>

                    {/* DTT 2 % */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.dtt2} onChange={v => setField(assortment, 'dtt2', v)} suffix="%" placeholder="0.0" min="0" max="100" step="0.1" />
                    </td>

                    {/* DTT 3 $ — flat dollar deduction */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.dtt3} onChange={v => setField(assortment, 'dtt3', v)} prefix="$" placeholder="0.00" min="0" step="0.01" />
                    </td>

                    {/* Net Sell Price — calculated */}
                    <td style={{ ...tdRight, color: c.netSellPrice != null ? 'var(--success)' : 'var(--border-soft)', fontWeight: 600, fontSize: 12 }}>
                      {fmt2(c.netSellPrice)}
                    </td>

                    {/* Marginal Cost */}
                    <td style={tdRight} onClick={e => e.stopPropagation()}>
                      <NumInput value={t.marginalCost} onChange={v => setField(assortment, 'marginalCost', v)} prefix="$" placeholder="0.00" min="0" />
                    </td>

                    {/* Retailer Margin % — calculated */}
                    <td style={{ ...tdRight, color: pctColor(c.retailerMarginPct), fontWeight: 600, fontSize: 12 }}>
                      {fmtPct(c.retailerMarginPct)}
                    </td>

                    {/* Our Margin % — calculated */}
                    <td style={{ ...tdRight, color: pctColor(c.ourMarginPct), fontWeight: 600, fontSize: 12 }}>
                      {fmtPct(c.ourMarginPct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {assortments.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              Loading assortments from SCANv2…
            </div>
          )}
        </div>

        {/* Waterfall panel for selected assortment */}
        {selectedAssortment && (
          <div style={{ width: 280, flexShrink: 0 }}>
            <WaterfallPanel
              label={selectedAssortment}
              terms={getTerms(customer, selectedAssortment)}
              customer={customerLabel}
            />
          </div>
        )}
      </div>

      {/* Assortments entered count */}
      {assortments.length > 0 && (() => {
        const populated = assortments.filter(a => {
          const t = getTerms(customer, a);
          return t.listPrice && t.rrp;
        });
        if (!populated.length) return null;
        return (
          <div className="kpi-card" style={{ maxWidth: 240 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Assortments Entered
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{populated.length} / {assortments.length}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{customerLabel} · has RRP &amp; list price</div>
          </div>
        );
      })()}

      {/* ── Trade Spend Section ──────────────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: 0 }}>Trade Spend Budget</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Promotional &amp; marketing commitments by customer · shared, saved to the database</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {tsSaved && <span style={{ color: 'var(--success)', fontSize: 12 }}>Saved</span>}
            {/* Customer filter */}
            <select
              value={tsCustFilter}
              onChange={e => setTsCustFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}
            >
              <option value="">All Customers</option>
              {tsCustomers.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
            {/* FY filter */}
            <div style={{ display: 'flex', gap: 4 }}>
              {['All', ...TS_FYS].map(fy => (
                <button key={fy} onClick={() => setTsFyFilter(fy)} style={{ background: tsFyFilter === fy ? 'var(--info-soft)' : 'var(--border)', border: `1px solid ${tsFyFilter === fy ? 'var(--info-border)' : 'var(--border-soft)'}`, borderRadius: 10, color: tsFyFilter === fy ? 'var(--info)' : 'var(--text-muted)', padding: '2px 9px', fontSize: 11, cursor: 'pointer' }}>FY{fy === 'All' ? ' All' : fy}</button>
              ))}
            </div>
            {/* Activity filter */}
            <select
              value={tsActivityFilter}
              onChange={e => setTsActivityFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}
            >
              <option value="All">All Activities</option>
              {TS_ACTIVITIES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button
              onClick={tsAddEntry}
              style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, color: 'var(--info)', padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
            >
              + Add Entry
            </button>
          </div>
        </div>

        {/* KPI summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
          {[
            ['Total Spend', tsTotals.total, 'var(--text-primary)'],
            ['Fixed Spend', tsTotals.fixed, 'var(--info)'],
            ['Variable Spend', tsTotals.variable, 'var(--warn)'],
            ['Entries', tsFiltered.length, 'var(--text-body)'],
          ].map(([label, value, color]) => (
            <div key={label} className="kpi-card">
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color }}>
                {typeof value === 'number' && label !== 'Entries' ? `$${value.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : value}
              </div>
            </div>
          ))}
        </div>

        {/* Trade spend table */}
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100, fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                {['Customer', 'Project', 'Activity', 'Brand', 'From', 'To', 'Units', 'Unit Deal', 'Variable $', 'Fixed $', 'Total $', 'FY', 'Responsibility', 'Comments', ''].map((h, i) => (
                  <th key={i} style={{ padding: '7px 10px', color: 'var(--text-muted)', fontWeight: 500, textAlign: ['Variable $', 'Fixed $', 'Total $', 'Units', 'Unit Deal'].includes(h) ? 'right' : 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tsFiltered.map(entry => {
                const isEditing = tsEditId === entry.id;
                const total = calcTsTotal(entry);
                const inStyle = { background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 11, padding: '2px 5px' };

                if (isEditing && tsEditDraft) {
                  const d = tsEditDraft;
                  const setD = (field, val) => setTsEditDraft(p => ({ ...p, [field]: val }));
                  return (
                    <tr key={entry.id} style={{ background: 'var(--info-soft)', borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input value={d.custName || ''} onChange={e => setD('custName', e.target.value)} placeholder="Customer name" style={{ ...inStyle, width: 150 }} />
                          <input value={d.custCode || ''} onChange={e => setD('custCode', e.target.value)} placeholder="Code" style={{ ...inStyle, width: 80, fontSize: 10 }} />
                        </div>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={d.project || ''} onChange={e => setD('project', e.target.value)} placeholder="Project" style={{ ...inStyle, width: 120 }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={d.activity} onChange={e => setD('activity', e.target.value)} style={{ ...inStyle }}>
                          {TS_ACTIVITIES.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={d.brand} onChange={e => setD('brand', e.target.value)} style={{ ...inStyle }}>
                          {TS_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}><input type="date" value={d.from || ''} onChange={e => setD('from', e.target.value)} style={{ ...inStyle, width: 120 }} /></td>
                      <td style={{ padding: '6px 8px' }}><input type="date" value={d.to || ''} onChange={e => setD('to', e.target.value)} style={{ ...inStyle, width: 120 }} /></td>
                      <td style={{ padding: '6px 8px' }}><input type="number" value={d.units ?? ''} onChange={e => setD('units', e.target.value || null)} placeholder="—" style={{ ...inStyle, width: 60, textAlign: 'right' }} /></td>
                      <td style={{ padding: '6px 8px' }}><input type="number" value={d.unitDeal ?? ''} onChange={e => setD('unitDeal', e.target.value || null)} placeholder="—" step="0.01" style={{ ...inStyle, width: 65, textAlign: 'right' }} /></td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <div style={{ color: calcTsVariable(d) > 0 ? 'var(--warn)' : 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>
                          {calcTsVariable(d) > 0 ? `$${calcTsVariable(d).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—'}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>units × deal</div>
                      </td>
                      <td style={{ padding: '6px 8px' }}><input type="number" value={d.fixed ?? ''} onChange={e => setD('fixed', e.target.value || null)} placeholder="0" step="1" style={{ ...inStyle, width: 75, textAlign: 'right' }} /></td>
                      <td style={{ padding: '6px 8px', color: 'var(--success)', fontWeight: 600, textAlign: 'right' }}>
                        {calcTsTotal(d) != null ? `$${Number(calcTsTotal(d)).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={d.fy} onChange={e => setD('fy', e.target.value)} style={{ ...inStyle, width: 60 }}>
                          {TS_FYS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}><input value={d.responsibility || ''} onChange={e => setD('responsibility', e.target.value)} placeholder="Name" style={{ ...inStyle, width: 100 }} /></td>
                      <td style={{ padding: '6px 8px' }}><input value={d.comments || ''} onChange={e => setD('comments', e.target.value)} placeholder="Notes…" style={{ ...inStyle, width: 160 }} /></td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={tsCommitEdit} style={{ background: 'var(--success)', border: 'none', borderRadius: 4, color: '#fff', padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Save</button>
                          <button onClick={tsCancelEdit} style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 4, color: 'var(--danger)', padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                const fmtAud = v => v != null ? `$${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—';
                const fmtDt = s => { if (!s) return '—'; const d = new Date(s); return `${d.getDate()}/${d.getMonth()+1}/${String(d.getFullYear()).slice(-2)}`; };

                // Activity badge colour
                const actColor = entry.activity === 'Price Promotion' ? 'var(--ai)' : entry.activity === 'Customer Marketing' ? 'var(--info)' : 'var(--text-muted)';
                const actBg = entry.activity === 'Price Promotion' ? 'var(--ai-soft)' : entry.activity === 'Customer Marketing' ? 'var(--info-soft)' : 'var(--border)';

                return (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px' }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{entry.custName}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{entry.custCode}</div>
                    </td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{entry.project || '—'}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ background: actBg, color: actColor, borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>{entry.activity}</span>
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ background: 'var(--border)', color: entry.brand?.includes('Bhuja') ? 'var(--warn)' : 'var(--info)', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>{entry.brand}</span>
                    </td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDt(entry.from)}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDt(entry.to)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{entry.units ?? '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{entry.unitDeal ? `$${Number(entry.unitDeal).toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: calcTsVariable(entry) > 0 ? 'var(--warn)' : 'var(--text-muted)' }}>{fmtAud(calcTsVariable(entry) || null)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (entry.fixed || 0) > 0 ? 'var(--info)' : 'var(--text-muted)' }}>{fmtAud(entry.fixed || 0)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>{fmtAud(total)}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>FY{entry.fy}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{entry.responsibility || '—'}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)', maxWidth: 160 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={entry.comments}>{entry.comments || '—'}</div>
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => tsStartEdit(entry)} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-body)', padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
                        <button onClick={() => tsDeleteEntry(entry.id)} style={{ background: 'none', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-muted)', padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Totals row */}
              {tsFiltered.length > 0 && (
                <tr style={{ background: 'var(--border)', borderTop: '2px solid var(--border)' }}>
                  <td colSpan={8} style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600 }}>TOTAL ({tsFiltered.length} entries)</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--warn)', fontWeight: 700 }}>${tsTotals.variable.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--info)', fontWeight: 700 }}>${tsTotals.fixed.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>${tsTotals.total.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>

          {tsFiltered.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              No entries match the current filters. Click <strong style={{ color: 'var(--info)' }}>+ Add Entry</strong> to get started.
            </div>
          )}
        </div>
      </div>

      {/* Footnote */}
      <div className="card" style={{ background: 'var(--bg-elevated)', padding: '12px 16px' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.8 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Off Invoice (OI) Discount</strong> — applied at point of invoice. Invoice Price = List Price × (1 − OI%).
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>DTT 1 & 2</strong> — % deferred trading terms off invoice price. DTT 3 — flat $ deduction (e.g. scan allowance, co-op). Net Sell = Invoice × (1 − DTT1% − DTT2%) − DTT3$.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Retailer Margin</strong> — (RRP ex-GST − Net Sell) ÷ RRP ex-GST. RRP entered inclusive of 10% GST; GST is stripped before calculation.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Our Margin</strong> — (Net Sell Price − Marginal Cost) ÷ Net Sell Price. Contribution margin as % of net sell price.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Marginal Cost</strong> — entered manually for now. PLANAUDIT integration planned to auto-populate from standard cost.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Persistence</strong> — saved to the shared commercial database (typed <code>commercial.TradeTerm</code> / <code>commercial.TradeSpend</code> tables), visible to the whole team. PLANAUDIT integration coming.
        </div>
      </div>
    </div>
  );
}
